"use client";

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslations } from "next-intl";
import { Link } from "@/navigation";
import {
  getConsentChoice,
  saveTrackingConsent,
  useHasConsentDecision,
} from "@/hooks/useTrackingConsent";
import { matchCrawlerUserAgent } from "@/lib/crawler-user-agent";
import type { TrackingConsent } from "@/utils/tracking/consent";

// Our cookie banner (replaces CookieYes). Mounted by the (site) layout only:
// guest upload pages never show it. It decides on the client after hydration,
// so the server HTML and the prerendered shells never contain it.
//
// Equal choices: «Reject all» and «Accept all» share one class (outlined
// slate pills, neither filled), so neither is the one to tap. It is docked to
// the bottom edge: on phones a strip of one question and two buttons (about
// 16 % of a 390×664 screen), on wider screens one bar at the page's width.
// The settings start unticked until the visitor has saved a choice (no
// pre-ticked consent); «Save choice» is the same pill as the other two. The
// settings sheet is a modal Radix dialog: focus moves to its heading, stays
// inside, and «Close», Escape or a tap on the scrim go back without saving,
// returning focus to the opener. While the banner shows, the page keeps its
// height free at the end, and bottom bars move up by --cookie-banner-space so
// the banner never covers a button (ReplySky #142).

const REJECT_ALL: TrackingConsent = { analytics: false, advertisement: false };
const ACCEPT_ALL: TrackingConsent = { analytics: true, advertisement: true };
const BANNER_SPACE_PROPERTY = "--cookie-banner-space";

export const CHOICE_BUTTON_CLASS =
  "h-11 rounded-full border-[1.5px] border-[#3f4152] bg-white px-5 text-sm font-semibold text-[#3f4152] transition-colors hover:bg-[#3f4152]/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3f4152] sm:min-w-44 sm:text-base";
const CHECKBOX_CLASS = "h-4 w-4 shrink-0 accent-[#3f4152]";

// Opens the settings; the opener gets focus back when they close.
type OpenSettings = (opener?: HTMLElement | null) => void;
const CookieSettingsContext = createContext<OpenSettings | null>(null);

function subscribeToNothing(): () => void {
  return () => {};
}

function isCrawlerBrowser(): boolean {
  return matchCrawlerUserAgent(navigator.userAgent) !== null;
}

function CookieSettingsButton({
  className,
  onOpen,
}: {
  className?: string;
  onOpen: OpenSettings;
}): React.JSX.Element {
  const t = useTranslations("CookieBanner");
  return (
    <button type="button" className={className} onClick={(event) => onOpen(event.currentTarget)}>
      {t("footerLink")}
    </button>
  );
}

/** «Cookie settings» for the footer; renders nothing outside the site layout. */
export function CookieSettingsLink({
  className,
}: {
  className?: string;
}): React.JSX.Element | null {
  const open = useContext(CookieSettingsContext);
  if (!open) return null;
  return <CookieSettingsButton className={className} onOpen={open} />;
}

export function CookieConsentProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const t = useTranslations("CookieBanner");
  const tFooter = useTranslations("Footer");
  const hasDecision = useHasConsentDecision();
  // Server and hydration: treated as a crawler, so nothing renders until the
  // browser is known.
  const isCrawler = useSyncExternalStore(subscribeToNothing, isCrawlerBrowser, () => true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [draft, setDraft] = useState<TrackingConsent>(REJECT_ALL);
  const [bannerSpace, setBannerSpace] = useState(0);
  const bannerRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  // The strip stays mounted under an open sheet, so focus can return to its
  // «Settings» when the sheet closes.
  const isVisible = !isCrawler && !hasDecision;
  const isSheetOpen = !isCrawler && isSettingsOpen;

  useEffect(() => {
    const element = bannerRef.current;
    if (!isVisible || !element) return;
    const root = document.documentElement;
    const measure = (): void => {
      const space = Math.ceil(element.getBoundingClientRect().height);
      setBannerSpace(space);
      root.style.setProperty(BANNER_SPACE_PROPERTY, `${space}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
      setBannerSpace(0);
      root.style.removeProperty(BANNER_SPACE_PROPERTY);
    };
  }, [isVisible]);

  function openSettingsView(opener?: HTMLElement | null): void {
    openerRef.current = opener ?? null;
    setDraft(getConsentChoice() ?? REJECT_ALL);
    setIsSettingsOpen(true);
  }

  function close(): void {
    setIsSettingsOpen(false);
  }

  function choose(consent: TrackingConsent): void {
    saveTrackingConsent(consent);
    close();
  }

  const privacyLink = (chunks: React.ReactNode): React.JSX.Element => (
    <Link
      href={{ pathname: "/privacy-policy", hash: "cookies" }}
      className="underline underline-offset-2"
    >
      {chunks}
    </Link>
  );

  const consentRow = (
    name: "analytics" | "advertising",
    checked: boolean,
    onChange: (checked: boolean) => void,
  ): React.JSX.Element => (
    <label className="flex min-h-11 cursor-pointer items-start gap-3 py-1">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className={`mt-0.5 sm:mt-1 ${CHECKBOX_CLASS}`}
      />
      <span className="flex flex-col">
        <span>{t(name)}</span>
        <span className="text-xs text-stone-500 sm:text-sm">{t(`${name}Vendors`)}</span>
      </span>
    </label>
  );

  return (
    <CookieSettingsContext.Provider value={openSettingsView}>
      {isVisible ? (
        <section
          ref={bannerRef}
          aria-labelledby={titleId}
          data-cookie-banner=""
          className="fixed inset-x-0 bottom-0 z-40 border-t border-stone-300 bg-white text-[#3f4152] shadow-[0_-6px_16px_-10px_rgba(31,42,68,0.3)] sm:shadow-none"
        >
          <div
            data-cookie-banner-content=""
            className="mx-auto flex max-w-[900px] flex-col gap-1.5 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:items-center sm:justify-center sm:gap-6 sm:px-6 sm:py-3.5"
          >
            <h2 id={titleId} className="sr-only">
              {t("title")}
            </h2>
            {/* One left-aligned run: the question, then its two links. */}
            <p className="text-sm leading-5 sm:text-base">
              {t("question")}{" "}
              <button
                type="button"
                onClick={(event) => openSettingsView(event.currentTarget)}
                className="mr-3 whitespace-nowrap underline underline-offset-2"
              >
                {t("settings")}
              </button>{" "}
              <Link
                href={{ pathname: "/privacy-policy", hash: "cookies" }}
                className="whitespace-nowrap underline underline-offset-2"
              >
                {tFooter("privacyPolicy")}
              </Link>
            </p>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
              <button
                type="button"
                onClick={() => choose(REJECT_ALL)}
                className={CHOICE_BUTTON_CLASS}
              >
                {t("reject")}
              </button>
              <button
                type="button"
                onClick={() => choose(ACCEPT_ALL)}
                className={CHOICE_BUTTON_CLASS}
              >
                {t("accept")}
              </button>
            </div>
          </div>
        </section>
      ) : null}
      <Dialog.Root open={isSheetOpen} onOpenChange={(isOpen) => (isOpen ? undefined : close())}>
        <Dialog.Portal>
          {/* Above the site header (z-50), so the page reads as paused. */}
          <Dialog.Overlay data-cookie-scrim="" className="fixed inset-0 z-[60] bg-[#1f2a44]/25" />
          <Dialog.Content
            data-cookie-sheet=""
            aria-modal="true"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              titleRef.current?.focus();
            }}
            // Radix returns focus to a Dialog.Trigger; our openers are the
            // strip's «Settings» and the footer link, so focus goes back there
            // (if a choice removed the strip, the browser keeps its default).
            // One tick later: the focus trap is still active during this call.
            onCloseAutoFocus={(event) => {
              const opener = openerRef.current;
              openerRef.current = null;
              if (opener?.isConnected) {
                event.preventDefault();
                setTimeout(() => opener.focus(), 0);
              }
            }}
            className="fixed inset-x-0 bottom-0 z-[60] max-h-[90dvh] overflow-y-auto overscroll-contain rounded-t-[18px] border border-b-0 border-stone-300 bg-white text-[#3f4152] shadow-[0_-12px_32px_-20px_rgba(0,0,0,0.4)] focus:outline-none"
          >
            <div className="mx-auto max-w-xl px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-4 sm:px-6 sm:pt-5">
              <div className="flex items-start justify-between gap-3">
                <Dialog.Title
                  ref={titleRef}
                  tabIndex={-1}
                  className="text-base font-semibold focus:outline-none sm:text-lg"
                >
                  {t("title")}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="-m-2 p-2 text-sm underline underline-offset-2 sm:text-base"
                  >
                    {t("close")}
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="mt-1 text-sm leading-5 text-stone-600 sm:text-[15px] sm:leading-6">
                {t.rich("details", { link: privacyLink })}
              </Dialog.Description>
              <fieldset className="mt-2 text-sm sm:text-[15px]">
                <legend className="sr-only">{t("categories")}</legend>
                <label className="flex min-h-11 items-start gap-3 py-1 text-stone-500">
                  <input type="checkbox" checked disabled className={`mt-0.5 sm:mt-1 ${CHECKBOX_CLASS}`} />
                  {t("necessary")}
                </label>
                {consentRow("analytics", draft.analytics, (checked) =>
                  setDraft({ ...draft, analytics: checked }),
                )}
                {consentRow("advertising", draft.advertisement, (checked) =>
                  setDraft({ ...draft, advertisement: checked }),
                )}
              </fieldset>
              {/* A custom choice is as easy as the other two: same pill. */}
              <button
                type="button"
                onClick={() => choose(draft)}
                className={`mt-2 w-full ${CHOICE_BUTTON_CLASS}`}
              >
                {t("save")}
              </button>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => choose(REJECT_ALL)}
                  className={CHOICE_BUTTON_CLASS}
                >
                  {t("reject")}
                </button>
                <button
                  type="button"
                  onClick={() => choose(ACCEPT_ALL)}
                  className={CHOICE_BUTTON_CLASS}
                >
                  {t("accept")}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      {children}
      {/* Room at the page's end, so its last controls scroll above the banner. */}
      {bannerSpace > 0 ? (
        <div aria-hidden="true" data-cookie-banner-space="" style={{ height: bannerSpace }} />
      ) : null}
    </CookieSettingsContext.Provider>
  );
}
